import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import { ContentResultEventlog } from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { StudentParticipationRelationship } from '@chancenwerk/db/semantics/StudentParticipationRelationship.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { DateTime } from 'luxon';
import { createHash } from 'node:crypto';

async function learningDataAggregation(filter: LearningDataExportFilter) {
  const coll = await getRawCollection<EntityData<ContentResultEventlog>>(ContentResultEventlog._collectionName);

  const agg = coll.aggregate<
    EntityData<ContentResultEventlog> & { kpfKuerzel: string; user: Student; testNumber: number }
  >([
    // Match firt parameters like the timewindow
    {
      $match: {
        _class: ContentResultEventlog._class,
      },
    },
    ...(filter.startDate
      ? [
          {
            $match: {
              createdAt: {
                $gte: filter.startDate.toJSDate(),
              },
            },
          },
        ]
      : []),
    ...(filter.endDate
      ? [
          {
            $match: {
              createdAt: {
                $lte: filter.endDate.toJSDate(),
              },
            },
          },
        ]
      : []),

    // most of the filters are on users and their kpfs so look them up
    {
      $lookup: {
        from: Student._collectionName,
        localField: 'userID',
        foreignField: '_id',
        as: 'user',
        pipeline: [
          {
            $project: {
              _class: 1,
              _id: 1,
              schoolInformation: {
                jahrgangsstufe: 1,
              },
              vorname: 1,
              nachname: 1,
              levumiHasMerged: 1,
            },
          },
        ],
      },
    },
    {
      $unwind: '$user',
    },
    // we only want users of the student class, because other users can also play exercises
    // check the grade at this point
    {
      $match: {
        'user._class': Student._class,
      },
    },
    ...(filter.grades?.length
      ? [
          {
            $match: {
              'user.schoolInformation.jahrgangsstufe': { $in: filter.grades },
            },
          },
        ]
      : []),
    // Get relations and kpfs
    {
      $lookup: {
        from: StudentParticipationRelationship._collectionName,
        localField: 'userID',
        foreignField: 'subjectID',
        as: 'relations',
      },
    },
    {
      $lookup: {
        from: KPF._collectionName,
        localField: 'relations.objectID',
        foreignField: '_id',
        as: 'kpfs',
        pipeline: [
          {
            $project: {
              _id: 1,
              schoolID: 1,
              produkt: {
                _class: 1,
              },
            },
          },
        ],
      },
    },
    // Match specific kpfs
    ...(filter.kpfIDs?.length
      ? [
          {
            $match: {
              kpfs: {
                $elemMatch: {
                  _id: { $in: filter.kpfIDs },
                },
              },
            },
          },
        ]
      : []),

    // get the schools of the kpf
    {
      $lookup: {
        from: Partner._collectionName,
        localField: 'kpfs.schoolID',
        foreignField: '_id',
        as: 'partners',
        pipeline: [
          {
            $project: {
              _id: 1,
              kuerzel: 1,
              bundesland: 1,
              address: {
                zipCity: {
                  city: 1,
                },
              },
            },
          },
        ],
      },
    },

    // Filter position of partner
    ...(filter.cities?.length
      ? [
          {
            $match: {
              partners: {
                $elemMatch: {
                  'address.zipCity.city': { $in: filter.cities },
                },
              },
            },
          },
        ]
      : []),
    ...(filter.bundeslaender?.length
      ? [
          {
            $match: {
              partners: {
                $elemMatch: {
                  bundesland: { $in: filter.bundeslaender },
                },
              },
            },
          },
        ]
      : []),

    { $addFields: { firstPartner: { $arrayElemAt: ['$partners', 0] } } },
    { $addFields: { firstKpf: { $arrayElemAt: ['$kpfs', 0] } } },

    { $addFields: { partionSpecifier: { $concat: ['$userID', '$contentID'] } } },
    // sort so we can rank the tests
    {
      $setWindowFields: {
        partitionBy: '$partionSpecifier',
        sortBy: { createdAt: 1 },
        output: {
          testNumber: {
            $sum: 1,
            window: {
              documents: ['unbounded', 'current'],
            },
          },
        },
      },
    },

    {
      $addFields: {
        kpfKuerzel: {
          $concat: [
            '$firstPartner.kuerzel',
            '_',
            {
              $substr: ['$firstKpf.produkt._class', { $subtract: [{ $strLenCP: '$firstKpf.produkt._class' }, 3] }, 3],
            },
          ],
        },
      },
    },
    {
      $project: {
        _id: 1,
        userID: 1,
        lessonID: 1,
        contentID: 1,
        createdAt: 1,
        kpfKuerzel: 1,
        user: {
          schoolInformation: {
            jahrgangsstufe: 1,
          },
          vorname: 1,
          nachname: 1,
          levumiHasMerged: 1,
        },
        type: 1,
        answer: 1,
        result: 1,
        levumiResultInformation: {
          levumiLevel: {
            subject: 1,
            level: 1,
          },
          levumiMathCategory: 1,
          overallAnswered: 1,
          correctlyAnswered: 1,
          correctlyAnsweredPercent: 1,
          readingAccuracyInPercent: 1,
          readingSpeedInQuePerMin: 1,
        },
        testNumber: 1,
      },
    },
  ]);

  return agg.toArray();
}

function removeEmptyCellsFromCSV(csvString: string) {
  const lines = csvString.split('\n');

  const cleanedLines = lines.map(line => {
    const cells = line.split(';');

    const cleanedCells = cells.map(cell => {
      if (cell.trim() === '') {
        return '';
      } else {
        return cell;
      }
    });

    return cleanedCells.join(';');
  });

  const cleanedCSV = cleanedLines.join('\n');

  return cleanedCSV;
}

function getExerciseTypeByValue(value: number) {
  return Object.keys(ExerciseType).find(
    key => ExerciseType[key as keyof typeof ExerciseType] === value,
  ) as keyof typeof ExerciseType;
}

// We has the user id in the export to make it anonymous. We dont want to trace back to different users
function hashUserID(userID: string) {
  const hash = createHash('sha256');
  hash.update(userID);
  return hash.digest('hex');
}

function cleanHTMLFieldForExport(htmlContent: string): string {
  // return htmlContent;
  const htmlCleanPattern = /(<\/?p>)|(<\/?br>)|\n/g;
  return htmlContent
    .replace(htmlCleanPattern, ' ')
    .replaceAll(/&gt;/g, '>')
    .replaceAll(/&nbsp;/g, ' ')
    .trim();
}

/**
 * @entity LearningDataExportFilter
 */
@Entity()
export class LearningDataExportFilter extends AdornisFilter {
  public static override _class = 'LearningDataExportFilter';

  @Field(type => [Int], {
    default: v => v ?? [],
  })
  public grades: Maybe<number[]>;

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public bundeslaender: Maybe<string[]>;

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public cities: Maybe<string[]>;

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public kpfIDs: Maybe<string[]>;

  @Field(type => DateTime)
  public startDate: Maybe<DateTime>;

  @Field(type => DateTime)
  public endDate: Maybe<DateTime>;

  constructor(doc: PartialEntityData<LearningDataExportFilter>) {
    super(doc);
  }
}

const resolveExportLearningDataWithFilter = (filter: LearningDataExportFilter) => {
  return async () => {
    const data = await learningDataAggregation(filter);
    return removeEmptyCellsFromCSV(
      `_id;userID;lessonID;exerciseID;testNumber;date;kpf;grade;type;answer;result;levumi_subject;levumi_level;levumi_math_category;levumi_overall_answered;levumi_correctly_answered;levumi_correctly_answered_percent;levumi_reading_accuracy;levumi_reading_speed_in_questions_per_minute;has_merged_data\n` +
        data
          .map(
            doc =>
              `${doc._id};${hashUserID(doc.userID ?? '')};${doc.lessonID};${doc.contentID};${
                doc.testNumber ?? ''
              };${DateTime.fromJSDate(doc.createdAt).toFormat('dd.MM.yyyy HH:mm')};${doc.kpfKuerzel};${
                doc.user.schoolInformation.jahrgangsstufe ?? ''
              };${getExerciseTypeByValue(doc.type)};"${cleanHTMLFieldForExport(doc.answer)}";${doc.result};${
                doc.levumiResultInformation.levumiLevel?.subject ?? ''
              };${doc.levumiResultInformation.levumiLevel?.level ?? ''};${
                doc.levumiResultInformation.levumiMathCategory ?? ''
              };${doc.levumiResultInformation.overallAnswered ?? ''};${
                doc.levumiResultInformation.correctlyAnswered ?? ''
              };${(doc.levumiResultInformation.correctlyAnsweredPercent?.toFixed(2) ?? '').replace('.', ',')};${(
                doc.levumiResultInformation.readingAccuracyInPercent?.toFixed(2) ?? ''
              ).replace('.', ',')};${(doc.levumiResultInformation.readingSpeedInQuePerMin?.toFixed(2) ?? '').replace(
                '.',
                ',',
              )};${!!doc.user.levumiHasMerged ? 'ja' : 'nein'}`,
          )
          .join('\n'),
    );
  };
};

export const exportLearningDataWithFilter = registerMutation({
  type: () => String,
  operationName: 'exportLearningDataWithFilter',
  params: [
    {
      type: () => LearningDataExportFilter,
      name: 'filter',
    },
  ],
  resolve: resolveExportLearningDataWithFilter,
});

const resolveExportLearningDataMappingWithFilter = (filter: LearningDataExportFilter) => {
  return async () => {
    const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
      roles: { name: 1, permissions: 1, contexts: 1 },
    });

    if (!user?.hasPermission(ChancenPermission.campus.EXPORT_LEARNING_DATA_MAPPING)) {
      throw new Error('You are not allowed to export this data');
    }

    const data = await learningDataAggregation(filter);
    return removeEmptyCellsFromCSV(
      `_id;hash;userID;vorname;nachname\n` +
        data
          .map(
            doc => `${doc._id};${hashUserID(doc.userID ?? '')};${doc.userID};${doc.user.vorname};${doc.user.nachname}`,
          )
          .join('\n'),
    );
  };
};

export const exportLearningDataMappingWithFilter = registerMutation({
  type: () => String,
  operationName: 'exportLearningDataMappingWithFilter',
  params: [
    {
      type: () => LearningDataExportFilter,
      name: 'filter',
    },
  ],
  resolve: resolveExportLearningDataMappingWithFilter,
});
